import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { mobileDigitsOnly, mobileValidationError } from '@uniqcamp/shared';
import { useToast } from '../../contexts/ToastContext';

export interface GuardianForm {
  name: string;
  mobile: string;
  relation: string;
  photo: string;
  canRequestDispersal: boolean;
  /** @deprecated Legacy; use canRequestDispersal */
  canRequestDismissal?: boolean;
  /** One per student: this guardian can log in to the parent portal (mobile + optional school code + OTP). */
  isPrimaryContact?: boolean;
}

export interface StudentFormData {
  studentId: string;
  name: string;
  class: string;
  section: string;
  motherName: string;
  motherMobile: string;
  fatherName: string;
  fatherMobile: string;
  pickupMethod: string;
  guardians: { name: string; mobile: string; relation: string; photo: string; canRequestDispersal?: boolean; canRequestDismissal?: boolean; isPrimaryContact?: boolean }[];
}

interface StudentForForm {
  studentId?: string;
  name?: string;
  class?: string;
  section?: string;
  motherName?: string;
  motherMobile?: string;
  fatherName?: string;
  fatherMobile?: string;
  guardians?: { name: string; mobile: string; relation: string; photo?: string; canRequestDispersal?: boolean; canRequestDismissal?: boolean; isPrimaryContact?: boolean }[];
  pickupMethod?: string;
}

interface StudentFormProps {
  open: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  student: StudentForForm | null;
  classSections: { class: string; section: string }[];
  relationsList: string[];
  pickupOptions: { value: string; label: string }[];
  canEdit: boolean;
  saving: boolean;
  onSave: (data: StudentFormData) => void;
}

const emptyForm = () => ({
  studentId: '',
  name: '',
  class: '',
  section: '',
  motherName: '',
  motherMobile: '',
  fatherName: '',
  fatherMobile: '',
  pickupMethod: 'PARENT_PICKUP' as string,
  guardians: [] as GuardianForm[],
});

const StudentForm: React.FC<StudentFormProps> = ({ open, onClose, mode, student, classSections, relationsList, pickupOptions, canEdit, saving, onSave }) => {
  const { showToast } = useToast();
  const [singleForm, setSingleForm] = useState(emptyForm());

  React.useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && student) {
      setSingleForm({
        studentId: student.studentId ?? '',
        name: student.name ?? '',
        class: student.class ?? '',
        section: student.section ?? '',
        motherName: student.motherName ?? '',
        motherMobile: student.motherMobile ?? '',
        fatherName: student.fatherName ?? '',
        fatherMobile: student.fatherMobile ?? '',
        pickupMethod: (student.pickupMethod as string) ?? 'PARENT_PICKUP',
        guardians: (student.guardians ?? []).map((g) => ({
          name: g.name,
          mobile: g.mobile,
          relation: g.relation,
          photo: g.photo ?? '',
          canRequestDispersal: (g.canRequestDispersal ?? g.canRequestDismissal) !== false,
          isPrimaryContact: g.isPrimaryContact === true,
        })),
      });
    }
  }, [open, mode, student]);

  const addGuardian = () => {
    setSingleForm((f) => ({ ...f, guardians: [...f.guardians, { name: '', mobile: '', relation: relationsList[0] ?? '', photo: '', canRequestDispersal: true, isPrimaryContact: false }] }));
  };
  const removeGuardian = (idx: number) => {
    setSingleForm((f) => ({ ...f, guardians: f.guardians.filter((_, i) => i !== idx) }));
  };
  const updateGuardian = (idx: number, field: keyof GuardianForm, value: string | boolean) => {
    setSingleForm((f) => {
      const next = [...f.guardians];
      next[idx] = { ...next[idx], [field]: value };
      return { ...f, guardians: next };
    });
  };

  const setGuardianPrimaryContact = (idx: number, checked: boolean) => {
    setSingleForm((f) => ({
      ...f,
      guardians: f.guardians.map((g, i) => ({
        ...g,
        isPrimaryContact: Boolean(checked && i === idx),
      })),
    }));
  };

  const handleSave = () => {
    if (mode === 'add') {
      if (!singleForm.studentId.trim() || !singleForm.name.trim() || !singleForm.class.trim()) {
        showToast('error', 'Student ID, name and class are required.');
        return;
      }
    } else {
      if (!singleForm.name.trim() || !singleForm.class.trim()) {
        showToast('error', 'Name and class are required.');
        return;
      }
    }
    const momErr = mobileValidationError(singleForm.motherMobile, { required: false, label: "Mother's mobile" });
    if (momErr) {
      showToast('error', momErr);
      return;
    }
    const dadErr = mobileValidationError(singleForm.fatherMobile, { required: false, label: "Father's mobile" });
    if (dadErr) {
      showToast('error', dadErr);
      return;
    }
    const guardiansOut = singleForm.guardians.filter((g) => g.name.trim() && g.mobile.trim());
    for (let i = 0; i < guardiansOut.length; i += 1) {
      const g = guardiansOut[i];
      const gErr = mobileValidationError(g.mobile, { required: true, label: `Guardian ${i + 1} mobile` });
      if (gErr) {
        showToast('error', gErr);
        return;
      }
    }
    onSave({
      studentId: singleForm.studentId.trim(),
      name: singleForm.name.trim(),
      class: singleForm.class.trim(),
      section: (singleForm.section || '').trim(),
      motherName: (singleForm.motherName || '').trim(),
      motherMobile: singleForm.motherMobile.trim() ? mobileDigitsOnly(singleForm.motherMobile) : '',
      fatherName: (singleForm.fatherName || '').trim(),
      fatherMobile: singleForm.fatherMobile.trim() ? mobileDigitsOnly(singleForm.fatherMobile) : '',
      pickupMethod: singleForm.pickupMethod,
      guardians: singleForm.guardians.filter((g) => g.name.trim() && g.mobile.trim()).map((g) => ({
        name: g.name.trim(),
        mobile: mobileDigitsOnly(g.mobile),
        relation: g.relation.trim() || 'Guardian',
        photo: (g.photo || '').trim(),
        canRequestDispersal: g.canRequestDispersal ?? g.canRequestDismissal,
        isPrimaryContact: g.isPrimaryContact === true,
      })),
    });
  };

  const readOnlyStudentId = mode === 'edit';

  const renderStudentForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <TextField
        label="Student ID"
        value={singleForm.studentId}
        onChange={(e) => setSingleForm((f) => ({ ...f, studentId: e.target.value }))}
        required
        disabled={readOnlyStudentId}
        placeholder="e.g. STU001"
      />
      <TextField
        label="Student Name"
        value={singleForm.name}
        onChange={(e) => setSingleForm((f) => ({ ...f, name: e.target.value }))}
        required
      />
      {classSections.length > 0 ? (
        <>
          <TextField
            select
            label="Class"
            value={singleForm.class}
            onChange={(e) => setSingleForm((f) => ({ ...f, class: e.target.value }))}
            required
          >
            <MenuItem value="">— Select —</MenuItem>
            {Array.from(new Set(classSections.map((cs) => cs.class))).sort().map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Section"
            value={singleForm.section}
            onChange={(e) => setSingleForm((f) => ({ ...f, section: e.target.value }))}
          >
            <MenuItem value="">— None —</MenuItem>
            {Array.from(new Set(classSections.filter((cs) => cs.class === singleForm.class).map((cs) => cs.section))).sort().map((sec) => (
              <MenuItem key={sec} value={sec}>{sec || '(blank)'}</MenuItem>
            ))}
          </TextField>
        </>
      ) : (
        <>
          <TextField
            label="Class"
            value={singleForm.class}
            onChange={(e) => setSingleForm((f) => ({ ...f, class: e.target.value }))}
            required
          />
          <TextField
            label="Section"
            value={singleForm.section}
            onChange={(e) => setSingleForm((f) => ({ ...f, section: e.target.value }))}
          />
        </>
      )}
      <TextField
        select
        label="Dispersal method"
        value={singleForm.pickupMethod}
        onChange={(e) => setSingleForm((f) => ({ ...f, pickupMethod: e.target.value }))}
      >
        {pickupOptions.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </TextField>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" fontWeight={600}>Guardians</Typography>
      <Typography variant="caption" color="text.secondary">
        Add parents or other guardians. Choose one primary contact for parent portal sign-in (mobile and OTP; school code optional on the parent login page). Dispersal permission controls who may request dispersal.
      </Typography>
      {singleForm.guardians.map((g, idx) => (
        <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            <TextField size="small" label="Name" value={g.name} onChange={(e) => updateGuardian(idx, 'name', e.target.value)} required sx={{ minWidth: 140 }} />
            <TextField size="small" label="Mobile" value={g.mobile} onChange={(e) => updateGuardian(idx, 'mobile', e.target.value)} placeholder="+91..." required sx={{ minWidth: 140 }} />
            <TextField
              size="small"
              select
              label="Relation"
              value={g.relation}
              onChange={(e) => updateGuardian(idx, 'relation', e.target.value)}
              sx={{ minWidth: 120 }}
            >
              {relationsList.length === 0 ? <MenuItem value="">Add relations in Staff → Relations</MenuItem> : relationsList.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Photo (URL)" value={g.photo} onChange={(e) => updateGuardian(idx, 'photo', e.target.value)} placeholder="Optional" sx={{ minWidth: 120 }} />
            <FormControlLabel
              control={<Switch checked={g.isPrimaryContact === true} onChange={(e) => setGuardianPrimaryContact(idx, e.target.checked)} size="small" />}
              label="Primary contact"
              sx={{ alignSelf: 'center' }}
            />
            <FormControlLabel
              control={<Checkbox checked={g.canRequestDispersal ?? g.canRequestDismissal} onChange={(e) => updateGuardian(idx, 'canRequestDispersal', e.target.checked)} size="small" />}
              label="Dispersal request permission"
            />
            <IconButton size="small" onClick={() => removeGuardian(idx)} color="error" title="Remove guardian"><DeleteIcon fontSize="small" /></IconButton>
          </Box>
        </Paper>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addGuardian} variant="outlined">Add guardian</Button>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'add' ? 'Add student' : 'Edit student'}</DialogTitle>
      <DialogContent>
        {renderStudentForm()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {canEdit && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {mode === 'add' ? 'Add' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StudentForm;
